/**
 * Readability primitives, shared by the custom-tool "readability" metric and
 * the public /readability-score-checker page.
 *
 * Zero-import leaf, for the same reason `metrics.ts` keeps a local `stripHtml`:
 * this ships to the browser, and must not drag a server dependency in behind
 * it.
 *
 * The word/sentence/syllable helpers were lifted verbatim out of
 * `customTools/metrics.ts` rather than rewritten, so the number a writer sees
 * on the public page is the number the in-app metric reports. Flesch is a
 * heuristic built for 1940s school textbooks; a "better" syllable counter here
 * would only make the two disagree.
 */

import { segmentSentences, segmentWords } from "@/lib/textSegmentation";

export const splitWords = (text: string, language = "en"): string[] => segmentWords(text, language);

export const splitSentences = (text: string, language = "en"): string[] => segmentSentences(text, language);

/** Syllable estimate. Approximate by nature — Flesch is a heuristic anyway. */
export function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (w.length <= 3) return w.length ? 1 : 0;
  const groups = w.replace(/e$/, "").match(/[aeiouy]+/g);
  return Math.max(1, groups?.length ?? 1);
}

/** Flesch reading ease. Higher is easier; roughly 0–100 but not clamped. */
export function fleschReadingEase(
  wordCount: number,
  sentenceCount: number,
  syllableCount: number,
): number {
  if (!wordCount || !sentenceCount) return 0;
  return 206.835 - 1.015 * (wordCount / sentenceCount) - 84.6 * (syllableCount / wordCount);
}

/** Flesch-Kincaid grade level — the same inputs, expressed as US school years. */
export function fleschKincaidGrade(
  wordCount: number,
  sentenceCount: number,
  syllableCount: number,
): number {
  if (!wordCount || !sentenceCount) return 0;
  return 0.39 * (wordCount / sentenceCount) + 11.8 * (syllableCount / wordCount) - 15.59;
}

export interface ReadabilityReport {
  words: number;
  sentences: number;
  syllables: number;
  /** Words of three or more syllables — Gunning Fog's "complex" words. */
  complexWords: number;
  avgSentenceLength: number;
  avgSyllablesPerWord: number;
  readingEase: number;
  gradeLevel: number;
  gunningFog: number;
  /** The longest sentences, longest first — the actionable part. */
  longestSentences: { text: string; words: number }[];
}

/** Every score for a block of prose, in one pass. */
export function analyzeReadability(text: string, longestCount = 3, language = "en"): ReadabilityReport {
  const sentences = splitSentences(text, language);
  const words = splitWords(text, language);
  let syllables = 0;
  let complexWords = 0;
  for (const w of words) {
    const s = countSyllables(w);
    syllables += s;
    if (s >= 3) complexWords++;
  }

  const wc = words.length;
  const sc = sentences.length;

  const longestSentences = sentences
    .map((s) => ({ text: s, words: splitWords(s, language).length }))
    .sort((a, b) => b.words - a.words)
    .slice(0, longestCount);

  return {
    words: wc,
    sentences: sc,
    syllables,
    complexWords,
    avgSentenceLength: sc ? wc / sc : 0,
    avgSyllablesPerWord: wc ? syllables / wc : 0,
    readingEase: fleschReadingEase(wc, sc, syllables),
    gradeLevel: fleschKincaidGrade(wc, sc, syllables),
    gunningFog: wc && sc ? 0.4 * (wc / sc + 100 * (complexWords / wc)) : 0,
    longestSentences,
  };
}

/**
 * The standard Flesch bands.
 *
 * Fiction is not the audience these were written for — most commercial novels
 * land in "fairly easy" to "easy", and a literary novel scoring "difficult" is
 * not thereby worse. The page says so; this just names the band.
 */
export function readingEaseBand(score: number): { label: string; easy: boolean } {
  if (score >= 90) return { label: "Very easy", easy: true };
  if (score >= 80) return { label: "Easy", easy: true };
  if (score >= 70) return { label: "Fairly easy", easy: true };
  if (score >= 60) return { label: "Plain English", easy: true };
  if (score >= 50) return { label: "Fairly difficult", easy: false };
  if (score >= 30) return { label: "Difficult", easy: false };
  return { label: "Very difficult", easy: false };
}
